import type { Point } from '@/types';

/**
 * Calculate a point on a quadratic bezier curve
 */
export function getQuadraticBezierPoint(
  start: Point,
  control: Point,
  end: Point,
  t: number
): Point {
  const x =
    (1 - t) * (1 - t) * start.x + 2 * (1 - t) * t * control.x + t * t * end.x;
  const y =
    (1 - t) * (1 - t) * start.y + 2 * (1 - t) * t * control.y + t * t * end.y;
  return { x, y };
}

/**
 * Calculate a point on a cubic bezier curve
 */
export function getCubicBezierPoint(
  start: Point,
  control1: Point,
  control2: Point,
  end: Point,
  t: number
): Point {
  const x =
    Math.pow(1 - t, 3) * start.x +
    3 * Math.pow(1 - t, 2) * t * control1.x +
    3 * (1 - t) * Math.pow(t, 2) * control2.x +
    Math.pow(t, 3) * end.x;

  const y =
    Math.pow(1 - t, 3) * start.y +
    3 * Math.pow(1 - t, 2) * t * control1.y +
    3 * (1 - t) * Math.pow(t, 2) * control2.y +
    Math.pow(t, 3) * end.y;

  return { x, y };
}

/**
 * Generate SVG path for a smooth curve through multiple points
 */
export function generateSmoothPath(points: Point[]): string {
  if (points.length < 2) return '';
  if (points.length === 2) {
    // Simple line for 2 points
    const p0 = points[0];
    const p1 = points[1];
    if (!p0 || !p1) return '';
    return `M ${p0.x} ${p0.y} L ${p1.x} ${p1.y}`;
  }

  let path = `M ${points[0]?.x} ${points[0]?.y}`;

  if (points.length === 3) {
    // Quadratic bezier for 3 points (start, control, end)
    const control = points[1];
    const end = points[2];
    if (control && end) {
      path += ` Q ${control.x} ${control.y} ${end.x} ${end.y}`;
    }
  } else {
    // For more points, create smooth curves using cubic bezier
    for (let i = 1; i < points.length; i++) {
      const current = points[i];
      const previous = points[i - 1];
      if (!current || !previous) continue;

      if (i === 1) {
        // First curve - use the second point as control
        const control = points[1];
        if (control) {
          path += ` Q ${control.x} ${control.y} ${current.x} ${current.y}`;
        }
      } else {
        // Subsequent curves - create smooth transitions
        const prevPoint = points[i - 2];
        const nextPoint = points[Math.min(i + 1, points.length - 1)];
        if (!prevPoint || !nextPoint) continue;

        const control1 = {
          x: previous.x + (current.x - prevPoint.x) * 0.3,
          y: previous.y + (current.y - prevPoint.y) * 0.3,
        };
        const control2 = {
          x: current.x - (nextPoint.x - previous.x) * 0.3,
          y: current.y - (nextPoint.y - previous.y) * 0.3,
        };
        path += ` C ${control1.x} ${control1.y} ${control2.x} ${control2.y} ${current.x} ${current.y}`;
      }
    }
  }

  return path;
}

/**
 * Calculate the middle control point for a simple bezier curve
 */
export function calculateMiddleControlPoint(
  start: Point,
  end: Point,
  offset: number = 0
): Point {
  const midX = (start.x + end.x) / 2;
  const midY = (start.y + end.y) / 2;

  // Add slight curve by offsetting perpendicular to the line
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.sqrt(dx * dx + dy * dy);

  if (length === 0) return { x: midX, y: midY };

  // Perpendicular vector
  const perpX = -dy / length;
  const perpY = dx / length;

  return {
    x: midX + perpX * offset,
    y: midY + perpY * offset,
  };
}

/**
 * Check if a point is near a bezier curve
 */
export function isPointNearBezierCurve(
  point: Point,
  curvePoints: Point[],
  tolerance: number = 5
): boolean {
  if (curvePoints.length < 2) return false;

  // Sample points along the curve and check distance
  const samples = 20;
  for (let i = 0; i <= samples; i++) {
    const t = i / samples;
    let curvePoint: Point;

    if (curvePoints.length === 2) {
      const p0 = curvePoints[0];
      const p1 = curvePoints[1];
      if (!p0 || !p1) continue;
      // Linear interpolation for straight line
      curvePoint = {
        x: p0.x + t * (p1.x - p0.x),
        y: p0.y + t * (p1.y - p0.y),
      };
    } else if (curvePoints.length === 3) {
      const p0 = curvePoints[0];
      const p1 = curvePoints[1];
      const p2 = curvePoints[2];
      if (!p0 || !p1 || !p2) continue;
      // Quadratic bezier
      curvePoint = getQuadraticBezierPoint(p0, p1, p2, t);
    } else if (curvePoints.length === 4) {
      const p0 = curvePoints[0];
      const p1 = curvePoints[1];
      const p2 = curvePoints[2];
      const p3 = curvePoints[3];
      if (!p0 || !p1 || !p2 || !p3) continue;
      // Cubic bezier
      curvePoint = getCubicBezierPoint(p0, p1, p2, p3, t);
    } else {
      // For more complex curves, sample the generated path
      // This is a simplified approach - in practice you'd evaluate the actual bezier
      const segmentIndex = Math.floor(t * (curvePoints.length - 1));
      const localT = t * (curvePoints.length - 1) - segmentIndex;
      const start = curvePoints[segmentIndex];
      const end =
        curvePoints[Math.min(segmentIndex + 1, curvePoints.length - 1)];

      if (!start || !end) continue;

      curvePoint = {
        x: start.x + localT * (end.x - start.x),
        y: start.y + localT * (end.y - start.y),
      };
    }

    const distance = Math.sqrt(
      Math.pow(point.x - curvePoint.x, 2) + Math.pow(point.y - curvePoint.y, 2)
    );

    if (distance <= tolerance) {
      return true;
    }
  }

  return false;
}

export function findInsertionPointOnPath(
  point: Point,
  pathPoints: Point[],
  tolerance: number = 10
): { position: Point; index: number } | null {
  if (pathPoints.length < 2) return null;

  let closestPoint: Point | null = null;
  let insertionIndex = -1;
  let minDistance = Infinity;

  for (let i = 0; i < pathPoints.length - 1; i++) {
    const start = pathPoints[i];
    const end = pathPoints[i + 1];
    if (!start || !end) continue;

    // Project point onto the line segment
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const lengthSq = dx * dx + dy * dy;

    if (lengthSq === 0) continue; // Skip zero-length segments

    const t = ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSq;
    const projection: Point = {
      x: start.x + t * dx,
      y: start.y + t * dy,
    };

    let closestOnSegment: Point;
    if (t < 0) {
      closestOnSegment = start;
    } else if (t > 1) {
      closestOnSegment = end;
    } else {
      closestOnSegment = projection;
    }

    const distance = Math.sqrt(
      Math.pow(point.x - closestOnSegment.x, 2) +
        Math.pow(point.y - closestOnSegment.y, 2)
    );

    if (distance < minDistance) {
      minDistance = distance;
      closestPoint = closestOnSegment;
      // The new point should be inserted after the current segment's start point
      insertionIndex = i + 1;
    }
  }

  if (minDistance < tolerance && closestPoint) {
    return {
      position: closestPoint,
      index: insertionIndex,
    };
  }

  return null;
}
